import { format } from "date-fns";
import { flatten, is, lensPath, set, uniq, uniqBy } from "ramda";
import { formatDateTimeFromISO } from "widgets";

import type {
  Attribute,
  AttributeDefaultValue,
  AttributeMultiValue,
  AttributeType,
  AttributeValue,
  DynamicTerm,
  DynamicVocabularyTerm,
  ExperimentInput,
  InputAttributeValueInput,
  InputEditSamplesInput,
  InputExperimentInput,
  InputSampleInput,
  InputSamplesValidationInputData,
  InputStatusForSampleType,
  Organism,
  Parent,
  ParentType,
  ParentTypeSet,
  ProjectFile,
  Sample,
  SampleType,
  VocabularyTerm,
} from "../graphql.js";
import { type IEditable, setEditCellValidation } from "../redux/modules/data-editing.js";
import type {
  AttributeDefaultValueType,
  ExperimentLevelAttributeValueType,
  IAttributeDefaultValueLookup,
  IAttributeLookup,
  IAttributeLookupState,
  IAttributesTemplatesLookup,
  IDefaultValuesLookup,
  ISampleEditingState,
  ISampleTypeAttributesTemplatesLookup,
  TSampleCellValue,
} from "../redux/modules/sample-editing.js";
import { findAttributeByKey } from "../redux/modules/sample-editing.js";
import type { IIdentifiable, ITableColumn } from "../redux/modules/table-data.js";
import {
  ATTRIBUTE_ID,
  ATTRIBUTE_TYPE,
  CELL_TYPE,
  COLUMN_DYNAMIC_TYPES,
  COLUMN_SET,
  COS_PARENT_ID,
  REPRESENTER_COLUMN,
} from "../settings/settings.js";
import type { IAttributeWithDefault } from "../ui-modules/admin/AddSampleTypeDialog.js";
import type { IExperimentValue } from "../ui-modules/playbooks/sample/CombinedRegistrationNgsReviewScreen.js";
import type { ISelectOption } from "./form.js";
import { removeTypename } from "./graphql.js";
import { normalizeForOracle } from "./oracle.js";
import type { IConfiguredAttribute } from "./playbook.js";
import { getTemplateDependentAttributes, removeAllTemplatePlaceholders } from "./sample-template.js";
import type { TGridSelModelIdentifier } from "./selection-model.js";
import { pluralizeWord } from "./string.js";
import {
  booleanValidation,
  controlledVocValidation,
  dateValidation,
  dynamicTermsValidation,
  fileAttributeValidation,
  freeTextValidation,
  hyperlinkValidation,
  numberValidation,
  whiteSpaceValidation,
} from "./validation.js";

const DEFAULT_SAMPLE_NAME_DATETIME_FORMAT = "ddMMMyyyy-HHmmss";

function validateFreeText(val: any, attribute: IConfiguredAttribute) {
  return whiteSpaceValidation(val) || freeTextValidation(val, attribute);
}
export interface IAttributeValueValidationResponse {
  attribute: Attribute;
  duplicates: string[];
}

export const getUniqueAttributeId = (a: Attribute): string => {
  return String(a.id);
};

export function getInitialDataRow(
  rowNum: number,
  attributes: Attribute[],
  defaultValues: IDefaultValuesLookup,
  sampleType: SampleType,
  templatesLookup: ISampleTypeAttributesTemplatesLookup,
  demoSample?: Sample,
  playbookSampleAttributeIds?: number[],
): IEditable {
  const row: IEditable = { id: `${new Date().getMilliseconds()}-${rowNum}` };

  attributes
    .filter((attribute: Attribute) => {
      const attributeKey: string = getUniqueAttributeId(attribute);

      // automatic attributes are in general filtered out, unless they have default/templates because they are
      // resolved on the client, they need to be part of the data for being processed and returned
      return !attribute.automatic || !!defaultValues[attributeKey];
    })
    .forEach((attribute: Attribute) => {
      const attributeKey: string = getUniqueAttributeId(attribute);

      // this will either be a demo value or a default (demo value will always have precedence)
      let valueToSet: TSampleCellValue;

      // Only set the demo value for attributes that are part of the playbook definition
      // There might be others used in other playbook definitions which would interfere with the workflow, SEL8-1993
      if (demoSample && !!playbookSampleAttributeIds?.find((i) => i === attribute.id)) {
        valueToSet = getSingleValueFromSample(demoSample, attribute);
      }

      // apply default only if no demo value is available
      if (!valueToSet && valueToSet !== 0) {
        let defaultValue = defaultValues[attributeKey];

        // replace the client side placeholders if the default is a template
        if (defaultValue && !is(Object, defaultValue) && !is(Boolean, defaultValue)) {
          defaultValue = removeAllTemplatePlaceholders(defaultValue);
        }

        // pre-populate sample names with standard default values
        if (!defaultValue && attribute.id === ATTRIBUTE_ID.SAMPLE_NAME) {
          defaultValue = `${format(new Date(), DEFAULT_SAMPLE_NAME_DATETIME_FORMAT).toUpperCase()}-sample${rowNum + 1}`;
        }

        valueToSet = defaultValue;
      }

      // if a demo/default value exist then we consider the value to be set/changed and
      //  therefore it will get part of the data registration
      row[attributeKey] = valueToSet || null;
      if (!row.editing) {
        row.editing = {};
      }
      row.editing[attributeKey] = {
        readOnly: !!templatesLookup[sampleType.id][attributeKey],
        didChange: !!valueToSet,
      };
    });

  return row;
}

export function computeDataGrid(
  samples: (Sample | Parent)[],
  attributes: Attribute[],
  templatesLookup: ISampleTypeAttributesTemplatesLookup,
  existingDataGrid: IEditable[],
): IEditable[] {
  return samples.map((sample: Sample) => {
    const existingSampleRowData = existingDataGrid.find((row) => row.id === sample.id);
    if (existingSampleRowData) {
      return existingSampleRowData;
    }
    const row: IEditable = { id: sample.id, locked: !!sample.locked };

    attributes.forEach((attr: Attribute) => {
      const attributeKey: string = getUniqueAttributeId(attr);

      const value: TSampleCellValue = getSingleValueFromSample(sample, attr);
      // if the cell corresponds to a sample having a template value (depends on sample type) then it is considered read-only
      const readOnly: boolean =
        !checkEditAttributeApplies(attr, sample) ||
        !!templatesLookup[sample.type.id][attributeKey] ||
        // make sure to handle boolean "false" values correctly: when set to false and edit_once, value should be readOnly
        ((!!value || value === false) && attr.editOnce);

      // access the attribute in sample and assign it to row
      row[attributeKey] = value;
      row.editing = {
        ...row.editing,
        [attributeKey]: {
          readOnly: readOnly,
          didChange: false,
        },
      };
    });

    return row;
  });
}

export function checkEditAttributeApplies(attribute: Attribute, sample: Sample) {
  return (
    !attribute.sampleTypes || !!attribute.sampleTypes.find((sampleType: SampleType) => sampleType.id === sample.type.id)
  );
}

export function validateRow(
  rowData: IEditable[],
  attributes: Attribute[],
  rowId: TGridSelModelIdentifier,
): IEditable[] {
  let newRowData = rowData;
  const row: IEditable = rowData.find((row) => row.id == rowId);

  attributes.forEach((attribute: Attribute) => {
    const attributeKey = getUniqueAttributeId(attribute);
    const readOnly = row.editing[attributeKey]?.readOnly;
    if (!readOnly) {
      const value: TSampleCellValue = row[attributeKey];
      const errorMessage: string = validateCellValue(value, attribute);
      newRowData = setEditCellValidation(newRowData, rowId, attribute.id, {
        validating: false,
        validationError: errorMessage,
      });
    }
  });

  return newRowData;
}

export function validateCellValue(value: TSampleCellValue, attribute: Attribute): string {
  if (!attribute) {
    return null; // this case can happen e.g. if a dependent attribute via template value is not part of the fetched attributes (e.g. analytical attributes during registration)
  }

  // specific validations
  if (attribute.id === ATTRIBUTE_ID.SAMPLE_NAME && (value as string).includes("/")) {
    return "Sample names must not contain slashes";
  }

  // generic validators based on attribute type
  const attributeType = attribute.attributeType.id;
  let validator: (value: TSampleCellValue, attribute: Attribute) => string = null;

  switch (attributeType) {
    case ATTRIBUTE_TYPE.FREE_TEXT:
      validator = validateFreeText;
      break;
    case ATTRIBUTE_TYPE.DYNAMIC_TERMS:
      validator = dynamicTermsValidation;
      break;
    case ATTRIBUTE_TYPE.CONTROLLED_TEXT:
      validator = controlledVocValidation;
      break;
    case ATTRIBUTE_TYPE.FREE_NUMERICAL:
    case ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL:
      validator = numberValidation;
      break;
    case ATTRIBUTE_TYPE.DATE:
      validator = dateValidation;
      break;
    case ATTRIBUTE_TYPE.HYPERLINK:
      validator = hyperlinkValidation;
      break;
    case ATTRIBUTE_TYPE.BOOLEAN:
      validator = booleanValidation;
      break;
    case ATTRIBUTE_TYPE.FILE:
      validator = fileAttributeValidation;
      break;
    default:
      validator = null;
  }

  return validator ? validator(value, attribute) : null;
}

export function createEditingGridColumnsFromAttributes(
  attributes: Attribute[],
  templatesLookup: IAttributesTemplatesLookup,
): ITableColumn[] {
  return attributes.map((srcAtt: Attribute) => {
    const dstCol: ITableColumn = {
      columnId: srcAtt.id,
      field: `${srcAtt.id}`,
      name: srcAtt.attributeName,
      groupName: srcAtt.groupName,
      //fixed: srcAtt.id === ATTRIBUTE_ID.SAMPLE_NAME,
      attribute: srcAtt,
      dynamicType: COLUMN_DYNAMIC_TYPES.TRACKER_ATTRIBUTE,
      editable: isAttributeEditable(srcAtt, templatesLookup),
    };

    switch (srcAtt.attributeType.id) {
      case ATTRIBUTE_TYPE.FREE_TEXT:
      case ATTRIBUTE_TYPE.HYPERLINK:
        dstCol.cellType = CELL_TYPE.TEXT;
        break;
      case ATTRIBUTE_TYPE.FREE_NUMERICAL:
        dstCol.cellType = CELL_TYPE.NUMBER;
        break;
      case ATTRIBUTE_TYPE.CONTROLLED_TEXT:
      case ATTRIBUTE_TYPE.DYNAMIC_TERMS:
        dstCol.cellType = CELL_TYPE.TEXT;
        break;
      case ATTRIBUTE_TYPE.DATE:
        dstCol.cellType = CELL_TYPE.DATE;
        break;
      case ATTRIBUTE_TYPE.BOOLEAN:
        dstCol.cellType = CELL_TYPE.BOOLEAN;
        break;
      case ATTRIBUTE_TYPE.ORGANISM:
        dstCol.cellType = CELL_TYPE.ORGANISM;
        dstCol.suppressPaste = true;
        dstCol.suppressFillHandle = true;
        break;
      case ATTRIBUTE_TYPE.FILE:
      case ATTRIBUTE_TYPE.FOLDER:
        dstCol.cellType = CELL_TYPE.FILE;
        dstCol.suppressPaste = true;
        dstCol.suppressFillHandle = true;
        break;
    }

    return dstCol;
  });
}

/**
 *  Extracts the vocabulary id for parent or sample attributes.
 */
function getVocabularyId(attribute: Attribute, value: string | ISelectOption): number {
  if (value && typeof value !== "string") {
    return value.value;
  }

  const field = attribute.vocabularyTerms.find((term: VocabularyTerm) => term.name === value);

  return field ? field.id : null;
}

/**
 * Extracts the dynamic term for a parent or sample attribute.
 */
function getDynamicTermId(attribute: Attribute, value: any): DynamicTerm {
  if (value.label != null) {
    value = value.label;
  }

  return removeTypename(attribute.dynamicTerms.find((term: DynamicTerm) => term.name === value)) as DynamicTerm;
}

export function createAttributeValueInputs(
  attributes: Attribute[],
  getValue: (attribute: Attribute) => TSampleCellValue | ExperimentLevelAttributeValueType,
): InputAttributeValueInput[] {
  // we flatten the list because an attribute value might consist of multiple values (e.g. ProjectFile)
  return flatten(
    attributes.map(
      (a: Attribute) => {
        const value = getValue(a);
        if (Array.isArray(value)) {
          if (!value.length) {
            return createAttributeValue(a, null); // create an attribute value that is meant to reset all multiple values
          }
          return (value as ProjectFile[]).map((file) => createAttributeValue(a, file));
        }
        return createAttributeValue(a, value);
      }, // create input object for attribute value
    ),
  );
}

function normalizeBooleanValueForStorage(value: boolean): number {
  switch (value) {
    case true:
      return 1;
    case false:
      return 0;
    default:
      return null;
  }
}

/**
 * Creates an input object for a parent or sample attribute with a value.
 */
export function createAttributeValue(
  attribute: Attribute,
  value: string | number | boolean | Organism | ISelectOption | ProjectFile,
): InputAttributeValueInput {
  const attributeTypeId = attribute.attributeType.id;
  return {
    attributeId: attribute.id,
    attributeTypeId,
    dateValue: attributeTypeId === ATTRIBUTE_TYPE.DATE ? (value as string) : null,
    numberValue:
      attributeTypeId === ATTRIBUTE_TYPE.FREE_NUMERICAL ||
      attributeTypeId === ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL ||
      attributeTypeId === ATTRIBUTE_TYPE.BOOLEAN ||
      attributeTypeId === ATTRIBUTE_TYPE.ORGANISM
        ? attributeTypeId === ATTRIBUTE_TYPE.BOOLEAN
          ? normalizeBooleanValueForStorage(value as boolean)
          : attributeTypeId === ATTRIBUTE_TYPE.ORGANISM
            ? (value as Organism)?.id
            : normalizeForOracle(value as number)
        : null,
    textValue:
      attributeTypeId === ATTRIBUTE_TYPE.FREE_TEXT || attributeTypeId === ATTRIBUTE_TYPE.HYPERLINK
        ? (value as string)?.trim().length
          ? (value as string)
          : ""
        : null,
    vocabularyTermId:
      attributeTypeId === ATTRIBUTE_TYPE.CONTROLLED_TEXT && value
        ? getVocabularyId(attribute, value as string | ISelectOption)
        : null,
    projectFilePath: attributeTypeId === ATTRIBUTE_TYPE.FILE && value ? (value as ProjectFile).fullPath : null,
    dynamicTerm: attributeTypeId === ATTRIBUTE_TYPE.DYNAMIC_TERMS && value ? getDynamicTermId(attribute, value) : null,
  };
}

export function getAttributeEncodedValue(attribute: Attribute, value: any): ExperimentLevelAttributeValueType {
  const attributeTypeId = attribute.attributeType.id;

  switch (attributeTypeId) {
    case ATTRIBUTE_TYPE.CONTROLLED_TEXT: {
      const vocTerm: VocabularyTerm = attribute.vocabularyTerms.find(
        (term: VocabularyTerm) => term.name === value || term.id === value,
      );

      return vocTerm && { value: vocTerm.id, label: vocTerm.name };
    }
    case ATTRIBUTE_TYPE.DYNAMIC_TERMS: {
      const dynTerm: DynamicTerm = value as DynamicTerm;
      return dynTerm && { value: dynTerm.id, label: dynTerm.name };
    }
    default:
      return value;
  }
}

export function getAttributeValueForDisplay(
  attribute: IConfiguredAttribute,
  attributeValue: ExperimentLevelAttributeValueType,
): IExperimentValue {
  const attributeName: string = attribute.label || attribute.attributeName;

  if (attributeValue !== null && attributeValue !== undefined) {
    // be cautious, a boolean value NO can be considered a falsy value
    switch (attribute.attributeType.id) {
      case ATTRIBUTE_TYPE.DATE: {
        const dateValue: string = attributeValue as string;
        return {
          title: attributeName,
          displayValue: formatDateTimeFromISO(dateValue, { hideTime: true }),
        };
      }
      case ATTRIBUTE_TYPE.BOOLEAN: {
        const booleanValue: boolean = attributeValue as boolean;
        return { title: attributeName, displayValue: booleanValue ? "yes" : "no" };
      }
      case ATTRIBUTE_TYPE.CONTROLLED_TEXT:
      case ATTRIBUTE_TYPE.DYNAMIC_TERMS: {
        const termValue: ISelectOption = attributeValue as ISelectOption;
        return { title: attributeName, displayValue: termValue.label };
      }
      case ATTRIBUTE_TYPE.GENOME_SELECTION: {
        if (!Array.isArray(attributeValue)) {
          // it's a selected genome and/or sequences
          const genome: Organism = attributeValue as Organism;
          return {
            title: attributeName,
            displayValue: `${genome ? `${genome.name}${genome.species && ` (${genome.species.name})`}` : ""}`,
          };
        }
        // its an (array of) paths
        const genomicFiles: ProjectFile[] = attributeValue as ProjectFile[];
        return {
          title: attributeName,
          displayValue: genomicFiles.map((file) => file.fileName).join(", "),
          tooltip: genomicFiles.map((file) => file.humanReadablePath).join(", "),
        };
      }
      case ATTRIBUTE_TYPE.FILE_OR_FOLDER:
      case ATTRIBUTE_TYPE.FILE:
      case ATTRIBUTE_TYPE.FOLDER: {
        const files: ProjectFile[] = attributeValue as ProjectFile[];
        return {
          title: attributeName,
          displayValue: files.map((file) => file.fileName).join(", "),
          tooltip: files.map((file) => file.humanReadablePath).join(", "),
        };
      }
      default: {
        const value = attributeValue as string | number;
        return {
          title: attributeName,
          displayValue: value,
        };
      }
    }
  }
}

function getDefaultValueByAttributeType(
  value: AttributeDefaultValue,
  attributeType: AttributeType,
): string | number | VocabularyTerm {
  switch (attributeType.id) {
    case ATTRIBUTE_TYPE.HYPERLINK: /* fallthrough */
    case ATTRIBUTE_TYPE.FREE_TEXT:
      return value.template ? value.template : value.defaultText;
    case ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL: /* fallthrough */
    case ATTRIBUTE_TYPE.FREE_NUMERICAL:
      return value.defaultNumber;
    case ATTRIBUTE_TYPE.CONTROLLED_TEXT:
      return value.defaultTerm;
    // BOOLEAN. DATE
    default:
      return undefined;
  }
}

export function computeScalarDefaultValues(
  sampleType: SampleType,
  defaultValues: IAttributeDefaultValueLookup,
): IDefaultValuesLookup {
  const primaryParentType: ParentType = getPrimaryParentType(sampleType);
  const defaultValuesLookup: IDefaultValuesLookup = {};
  Object.keys(defaultValues).map((attributeKey: string) => {
    const defaultValue: AttributeDefaultValue = defaultValues[attributeKey];
    const attribute: Attribute = findAttributeByKey(attributeKey, sampleType, primaryParentType);
    if (attribute) {
      let d: AttributeDefaultValueType = getDefaultValueByAttributeType(defaultValue, attribute.attributeType);
      // compute the stored default for this attribute
      // Make sure the vocabulary term is handled correctly.
      // we convert the default voc term
      // to a (name) string and retrieve the full object later again if required, e.g. for ReactSelect fields.
      if (ATTRIBUTE_TYPE.CONTROLLED_TEXT === attribute.attributeType.id && defaultValue.defaultTerm) {
        d = defaultValue.defaultTerm.name;
      } else if (
        (ATTRIBUTE_TYPE.FREE_NUMERICAL === attribute.attributeType.id ||
          ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL === attribute.attributeType.id) &&
        is(Number, defaultValue.defaultNumber)
      ) {
        d = String(d);
      }
      defaultValuesLookup[getUniqueAttributeId(attribute)] = d;
    }
  });
  return defaultValuesLookup;
}

// allows to set to valid/invalid the attributes that are used to build another value via template (e.g. Regeneron Sample Name) so that the uniqueness validation
// does also point to the dependent attribtues to be changed
export function handleInvalidCellWithUniquenessCheckContext(
  rowId: TGridSelModelIdentifier,
  attribute: Attribute,
  isValid: boolean,
  templatesLookup: IAttributesTemplatesLookup,
  editingGrid: IEditable[],
): IEditable[] {
  let gridData = editingGrid;

  const row: IEditable = gridData.find((row) => row.id == rowId);
  if (isValid) {
    // do not erase the error message, we first validate the cell itself and show an error message accordingly.
    // since the uniqueness check is done after the individual cell values validation, we might reset the validation message, this would be wrong
    const cell: TSampleCellValue = row[attribute.id];
    const cellErrorMessage: string = validateCellValue(cell, attribute);
    gridData = setEditCellValidation(gridData, rowId, attribute.id, { validationError: cellErrorMessage });
  } else {
    let nonUniqueMessage = "Each value must be unique.";

    if (templatesLookup[attribute.id]) {
      const dependentAttributes: Attribute[] = getTemplateDependentAttributes(templatesLookup, attribute.id);

      nonUniqueMessage += `The value is computed from ${pluralizeWord(
        "attribute",
        templatesLookup[attribute.id].length,
      )} `;
      nonUniqueMessage += dependentAttributes.map((att: Attribute) => att.attributeName).join(", ");
      nonUniqueMessage += ".";
    }
    gridData = setEditCellValidation(gridData, rowId, attribute.id, { validationError: nonUniqueMessage });
  }

  // extend the invalidity to all attributes involved in building the template value
  if (templatesLookup[attribute.id]) {
    const dependentAttributes: Attribute[] = getTemplateDependentAttributes(templatesLookup, attribute.id);

    dependentAttributes.forEach((templateAttribute: Attribute) => {
      const templateAttributeKey: string = getUniqueAttributeId(templateAttribute);

      if (isValid) {
        // same reason as above : if the cell is valid according to the uniquness check we should not simply reset the validation message because the cell value itself might be in error state
        const cell: TSampleCellValue = row[templateAttributeKey];
        const cellErrorMessage: string = validateCellValue(cell, templateAttribute);
        gridData = setEditCellValidation(gridData, rowId, templateAttribute.id, {
          validationError: cellErrorMessage,
        });
      } else {
        const uniqueComputationMessage = `${templateAttribute.attributeName} helps building the value of attribute ${attribute.attributeName}`;
        gridData = setEditCellValidation(gridData, rowId, templateAttribute.id, {
          validationError: uniqueComputationMessage,
        });
      }
    });
  }

  return gridData;
}

export function validateUniqueColumnsOnClient(
  editingGrid: IEditable[],
  attributes: Attribute[],
  templatesLookup: IAttributesTemplatesLookup,
): IEditable[] {
  let editingData = editingGrid;

  attributes
    .filter((att) => isAttributeEditable(att, templatesLookup))
    .filter((att) => att.valueUniqueConstraint)
    .forEach((att: Attribute) => {
      const attributeId: number = att.id;

      for (let rowIdx = 0; rowIdx < editingGrid.length; rowIdx++) {
        const row: IEditable = editingGrid[rowIdx];
        const value: any = row[attributeId]; //.value as string;
        const duplicateValue = !!editingGrid.find((row, index) => index !== rowIdx && row[attributeId] == value);
        editingData = handleInvalidCellWithUniquenessCheckContext(
          row.id,
          att,
          !duplicateValue,
          templatesLookup,
          editingData,
        );
      }
    });

  return editingData;
}

export function extractSamplesEditingInput(stateEditingState: ISampleEditingState): InputEditSamplesInput {
  const editingGrid: IEditable[] = stateEditingState.editingGrid;
  const attributeLookupState: IAttributeLookupState = stateEditingState;

  const samples: InputSampleInput[] = editingGrid
    //.filter((e) => !e.locked) // do not send any data for locked samples to the MT
    .map((dataRow: IEditable) => {
      return extractSampleInput(dataRow, attributeLookupState, stateEditingState.editedSamples);
    });
  let statusForSampleTypes: InputStatusForSampleType[];
  if (stateEditingState.status) {
    const sampleTypeIds: number[] = uniq(stateEditingState.editedSamples.map((sample) => sample.type.id));
    statusForSampleTypes = sampleTypeIds.map((sampleTypeId) => ({
      statusId: stateEditingState.status.id,
      sampleTypeId: sampleTypeId,
    }));
  }
  return {
    samples: samples,
    statusForSampleTypes: statusForSampleTypes,
  };
}

export function extractSampleInput(
  dataRow: IEditable,
  lookupState: IAttributeLookupState,
  editedSamples: Sample[],
): InputSampleInput {
  const attributes = Object.keys(lookupState.attributeLookup)
    // only send modified values
    .filter((attributeKey: string) => !!dataRow.editing?.[attributeKey]?.didChange)
    .filter((attributeKey: string) => !lookupState.attributeLookup[attributeKey].isWorkflowParam) // workflow params are handled differently
    .map((attributeKey: string) => lookupState.attributeLookup[attributeKey]);

  const attributeInputs: InputAttributeValueInput[] = createAttributeValueInputs(attributes, (attribute) => {
    return dataRow[getUniqueAttributeId(attribute)];
  });

  const sampleInput: InputSampleInput = {
    sampleAttributes: attributeInputs,
  };

  // set id for update case
  if (editedSamples.find((sample) => sample.id == dataRow.id)) {
    sampleInput.sampleId = dataRow.id;
  }

  return sampleInput;
}

export function extractSamplesValidationInput(
  editingGrid: IEditable[],
  attribute: Attribute,
  editedSamples: Sample[],
): InputSamplesValidationInputData {
  const attributeKey: string = getUniqueAttributeId(attribute);

  return {
    attributeId: attribute.id,
    values: editingGrid
      .map((dataRow: IEditable) => dataRow[attributeKey] /*?.value as string*/)
      .filter((value) => !!value && value !== ""),
    sampleIds: editingGrid
      .map((dataRow: IEditable) => dataRow.id)
      .filter((sampleId) => editedSamples.find((sample) => sample.id == sampleId)),
  };
}

export function getRawAttributeValue(
  value: AttributeValue,
): string | boolean | number | Date | string[] | ProjectFile | DynamicVocabularyTerm {
  return getRawAttributeValueFromType(value, value?.attribute?.attributeType);
}

export function getRawAttributeValueFromType(
  value: AttributeValue,
  attributeType: AttributeType,
): string | boolean | number | Date | string[] | ProjectFile | DynamicVocabularyTerm {
  if (!attributeType) {
    return ""; // value has no attribute type
  }

  switch (attributeType.id) {
    case ATTRIBUTE_TYPE.BOOLEAN:
      return value.booleanValue;
    case ATTRIBUTE_TYPE.DATE:
      return value.dateValue;
    case ATTRIBUTE_TYPE.FREE_NUMERICAL:
    case ATTRIBUTE_TYPE.CALCULATED:
    case ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL:
      return value.floatValue;
    case ATTRIBUTE_TYPE.DYNAMIC_TERMS:
      return value.dynamicTerm;
    case ATTRIBUTE_TYPE.DERIVED_TERMS:
      return value.stringListValue;
    case ATTRIBUTE_TYPE.FILE:
      return value.projectFile;
    default:
      return value.stringValue;
  }
}
export function getRawAttributeValueFromTypeForGridEditing(
  value: AttributeValue,
  attributeType: AttributeType,
): string | boolean | number | Date | string[] | ProjectFile {
  if (!attributeType) {
    return ""; // value has no attribute type
  }
  switch (attributeType.id) {
    case ATTRIBUTE_TYPE.DYNAMIC_TERMS: // currently the dynamicTerm needs to point to the name directly in order to support copy/paste into editing grid
      return value.dynamicTerm.name;
    default:
      return getRawAttributeValueFromType(value, attributeType);
  }
}
export function getSingleValueFromSample(sample: Sample, attr: Attribute): TSampleCellValue {
  // TODO [SEL8-2003] seems like the valuesByAttributeId data does not provide name/description, and also not the parent name ?

  if (attr.representedColumn === REPRESENTER_COLUMN.NAME) {
    return sample.name;
  }

  if (attr.representedColumn === REPRESENTER_COLUMN.DESCRIPTION) {
    return sample.description;
  }

  if (attr.representedColumn === REPRESENTER_COLUMN.ORGANISM) {
    return sample.organism;
  }

  const attributeMultiValue: AttributeMultiValue = sample.valuesByAttributeId.find(
    (multiValueData) => multiValueData.attribute.id === attr.id,
  );
  const value = attributeMultiValue?.valueList.map((attributeValue) =>
    getRawAttributeValueFromTypeForGridEditing(attributeValue, attr.attributeType),
  );

  if (attr.attributeType.id === ATTRIBUTE_TYPE.FILE) {
    return value?.filter((projectFile) => !!projectFile) as ProjectFile[]; // because of multi value alignment some rows might be null
  }
  // in this case value can only be an array of primitives (file object tackled above)
  // all dynamic attributes have value arrays since the introduction of multi value support - here we take just the first element
  return value?.[0];
}

export function getHumanReadableAttributeValue(value: AttributeValue): string {
  if (!value?.attribute?.attributeType) {
    return ""; // value has no attribute type
  }

  switch (value.attribute.attributeType.id) {
    case ATTRIBUTE_TYPE.BOOLEAN:
      return getHumanReadableBooleanValue(value.booleanValue);
    case ATTRIBUTE_TYPE.DATE:
      return formatDateTimeFromISO(value.dateValue, { hideTime: true });
    case ATTRIBUTE_TYPE.DYNAMIC_TERMS:
      return value.dynamicTerm.name;
    case ATTRIBUTE_TYPE.DERIVED_TERMS:
      return value.stringListValue.join(",");
    case ATTRIBUTE_TYPE.FILE:
      return value.projectFile.fileName;
    default:
      return getRawAttributeValue(value);
  }
}

export function getHumanReadableBooleanValue(value: boolean | null): string {
  return value ? "yes" : "no";
}

export function getSampleSummaryCellType(value: AttributeValue): any {
  if (!value?.attribute?.attributeType) {
    return CELL_TYPE.STRING; // fallback
  }

  switch (value.attribute.attributeType.id) {
    case ATTRIBUTE_TYPE.BOOLEAN:
      return CELL_TYPE.STRING;
    case ATTRIBUTE_TYPE.DATE:
      return CELL_TYPE.STRING;
    case ATTRIBUTE_TYPE.DERIVED_TERMS:
      return CELL_TYPE.STRING;
    case ATTRIBUTE_TYPE.FREE_NUMERICAL:
    case ATTRIBUTE_TYPE.CALCULATED:
    case ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL:
      return CELL_TYPE.NUMBER;
    case ATTRIBUTE_TYPE.FILE:
    case ATTRIBUTE_TYPE.FILE_OR_FOLDER:
    case ATTRIBUTE_TYPE.FOLDER:
      return CELL_TYPE.FILE;
    default:
      return CELL_TYPE.STRING;
  }
}

export function cellParamsMatchesAttributeTypes(cellData: any, matchingAttributeTypes: any[]) {
  if (cellData) {
    return !!matchingAttributeTypes.find(
      (matchingAttributeType) => matchingAttributeType === cellData.attributeType.id,
    );
  }

  return false;
}

export function isRepresenterOrUsageAttribute(attribute: Attribute): boolean {
  return !!attribute.representedColumn || !!attribute.usage;
}

export function isAutomaticRepresenterAttribute(attribute: Attribute) {
  return (
    attribute.id === ATTRIBUTE_ID.SAMPLE_STATUS ||
    attribute.id === ATTRIBUTE_ID.SAMPLE_ADDED ||
    attribute.id === ATTRIBUTE_ID.SAMPLE_MODIFIED ||
    attribute.id === ATTRIBUTE_ID.SAMPLE_SUBMITTER ||
    attribute.id === ATTRIBUTE_ID.SAMPLE_RECEIVER ||
    attribute.id === ATTRIBUTE_ID.SAMPLE_TYPE ||
    attribute.id === ATTRIBUTE_ID.SAMPLE_PROJECT ||
    (attribute.columnSet.id === COLUMN_SET.COS_PARENT_ID.id &&
      attribute.representedColumn === REPRESENTER_COLUMN.STATUS)
  );
}

export function getPrimaryParentType(sampleType: SampleType): ParentType {
  const primaryParentTypeSet: ParentTypeSet = sampleType?.parentTypeSets?.find(
    (parentTypeSet: ParentTypeSet) => parentTypeSet.isPrimary,
  );
  return primaryParentTypeSet?.parentType;
}

export function isDynamicTerm(attribute: Attribute): boolean {
  return attribute?.attributeType?.id === ATTRIBUTE_TYPE.DYNAMIC_TERMS;
}

export function isScalarValue(attribute: Attribute): boolean {
  return attribute && !attribute.multipleValues;
}

export function isMultiValue(attribute: Attribute): boolean {
  return attribute && attribute.multipleValues;
}

export function isFileAttribute(attribute: Attribute): boolean {
  return attribute && attribute.attributeType && attribute.attributeType.id === ATTRIBUTE_TYPE.FILE;
}

export function mandatoryFieldHasNoDefault(attribute: Attribute, defaultValuesLookup: IDefaultValuesLookup): boolean {
  // we must curate the field string to make it compatible with the defaults lookup
  return attribute.mandatory && defaultValuesLookup && !defaultValuesLookup[getUniqueAttributeId(attribute)];
}

export function findAutomaticParentAttributeWithMissingDefault(
  attributes: IAttributeWithDefault[],
): IAttributeWithDefault {
  return attributes
    .filter((attribute: IAttributeWithDefault) => !!attribute.automatic)
    .filter(isConfigurableParentAttribute)
    .find((attribute: IAttributeWithDefault) => !attribute.attributeDefault);
}

export function isConfigurableParentAttribute(attribute: Attribute): boolean {
  return (
    attribute &&
    attribute.attributeType &&
    (attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_TEXT ||
      attribute.attributeType.id === ATTRIBUTE_TYPE.FREE_NUMERICAL ||
      attribute.attributeType.id === ATTRIBUTE_TYPE.CONTROLLED_TEXT) &&
    attribute.representedColumn !== REPRESENTER_COLUMN.STATUS &&
    !attribute.usage
  );
}

export function fieldIsConfiguredInPlaybook(attribute: Attribute, inputFields: InputExperimentInput[]): boolean {
  return !!inputFields.find(
    (twAtribute: ExperimentInput) =>
      attribute.id === (attribute.isWorkflowParam ? twAtribute.workflowParamId : twAtribute.trackerAttributeId),
  );
}

export function checkAttributeDefaults(attributes: IAttributeWithDefault[], parentType?: ParentType): string {
  if (!attributes) {
    return;
  }

  const invalidTextDefaultAttribute = attributes.find(
    (a: IAttributeWithDefault) =>
      a.attributeDefault?.defaultText?.trim().length > 0 && freeTextValidation(a.attributeDefault.defaultText, a),
  );

  if (invalidTextDefaultAttribute) {
    return `${getDefaultMessageCore(invalidTextDefaultAttribute, parentType)}: ${freeTextValidation(
      invalidTextDefaultAttribute.attributeDefault.defaultText,
      invalidTextDefaultAttribute,
    )}`;
  }
  const invalidNumberDefaultAttribute = attributes.find(
    (a: IAttributeWithDefault) =>
      (a.attributeDefault?.defaultNumber || a.attributeDefault?.defaultNumber === 0) &&
      numberValidation(a.attributeDefault.defaultNumber, a),
  );

  if (invalidNumberDefaultAttribute) {
    return `${getDefaultMessageCore(invalidNumberDefaultAttribute, parentType)}: ${numberValidation(
      invalidNumberDefaultAttribute.attributeDefault.defaultNumber,
      invalidNumberDefaultAttribute,
    )}`;
  }
  return;
}

function getDefaultMessageCore(attribute: IAttributeWithDefault, parentType: ParentType): string {
  return `Default of attribute "${attribute.attributeName}"${parentType ? ` (parent type "${parentType.name}")` : ""}`;
}

export function isAttributeEditable(attribute: Attribute, templatesLookup?: IAttributesTemplatesLookup) {
  if (
    !isAttributePartOfEditingGrid(attribute, templatesLookup) ||
    !attribute.modifiable ||
    !attribute.editable ||
    attribute.columnSet.id === COS_PARENT_ID
  ) {
    return false;
  }
  return true;
}

export function isAttributePartOfEditingGrid(attribute: Attribute, templatesLookup?: IAttributesTemplatesLookup) {
  if (attribute.columnSet.id === COS_PARENT_ID) {
    return false;
  } else if (attribute.multipleValues && attribute.attributeType.id !== ATTRIBUTE_TYPE.FILE) {
    return false;
  } else if (attribute.automatic) {
    const attributeKey = getUniqueAttributeId(attribute);
    return templatesLookup ? !templatesLookup[attributeKey] : false;
  }
  return true;
}

export function isNameAttribute(attribute: Attribute) {
  return attribute.representedColumn === REPRESENTER_COLUMN.NAME && attribute.columnSet.id !== COS_PARENT_ID;
}

export function makeAttributesMandatory(attributes: Attribute[], mandatoryAttributeIds: number[]) {
  return attributes.map((attribute) =>
    mandatoryAttributeIds?.find((mandatoryAttributeId) => mandatoryAttributeId === attribute.id)
      ? { ...(attribute as IConfiguredAttribute), correctedMandatory: true }
      : (attribute as IConfiguredAttribute),
  );
}

export function createHiddenAttributesWarning(allAttributes: Attribute[]): string {
  const notEditableMultiValueAttributes = allAttributes
    .filter((attribute) => !isAttributePartOfEditingGrid(attribute))
    .filter(isMultiValue);

  const nofMvAtts: number = notEditableMultiValueAttributes.length;
  const mvAttNames: string[] = notEditableMultiValueAttributes.map((a) => a.attributeName);
  const duplicates: string[] = mvAttNames.filter((item, index) => mvAttNames.indexOf(item) != index);

  return nofMvAtts > 0
    ? `The following multi-value ${pluralizeWord("attribute", nofMvAtts)} ${
        nofMvAtts === 1 ? "is" : "are"
      } not available for editing: ` +
        `${notEditableMultiValueAttributes
          .map((a) =>
            duplicates.find((d) => d === a.attributeName) ? `${a.groupName} - ${a.attributeName}` : a.attributeName,
          )
          .join(", ")}`
    : null;
}

export function createLockedSamplesWarning(lockedSampleCount: number): string | null {
  return lockedSampleCount > 0
    ? `The sample selection contains ${lockedSampleCount} locked ${pluralizeWord("sample", lockedSampleCount)}. Writing data to ${lockedSampleCount === 1 ? "this" : "these"} ${pluralizeWord("sample", lockedSampleCount)} will not be possible.`
    : null;
}

export function getSampleRowsForReviewScreen(data: IEditable[], visibleAttributes: Attribute[]) {
  return data.map((row: IEditable, index) => {
    let rowData: IIdentifiable = { id: index, locked: row.locked };
    visibleAttributes.forEach((attribute: Attribute) => {
      if (attribute) {
        const rowKey: string = getUniqueAttributeId(attribute);
        const value =
          attribute?.attributeType.id === ATTRIBUTE_TYPE.DATE
            ? (row[rowKey] as string) // it is responsibility of the cell to format the value correctly
            : attribute?.attributeType.id === ATTRIBUTE_TYPE.ORGANISM
              ? row[rowKey]?.name
              : row[rowKey];

        const rowField: string =
          attribute?.attributeType.id === ATTRIBUTE_TYPE.FILE
            ? attribute.tableColumn.groupField
            : attribute.tableColumn.field;
        // the rowField might of the form attribute12345.numberValue, which needs to be interpreted as a path
        rowData = set(lensPath(rowField.split(".")), value, rowData);
      }
    });
    return rowData;
  });
}

export function getCollidingSamplesWarning(samples: Sample[], selectedAttributes: Attribute[]) {
  const collidingAttributes: Attribute[] = [];
  let warningMessage: string;
  const collidingSamples: string[] = samples
    .filter((sample: Sample) => {
      let dataPresent = false;

      selectedAttributes.forEach((attribute) => {
        if (getSingleValueFromSample(sample, attribute)) {
          dataPresent = true;
          collidingAttributes.push(attribute);
        }
      });

      return dataPresent;
    })
    .map((s: Sample) => s.name);

  // if at least one sample with existing values was found, create a warning message and place it in the local state
  if (collidingSamples.length > 0) {
    const overriddenAttributeValuesCount = uniqBy((attribute) => attribute.id, collidingAttributes).length;
    warningMessage = `${pluralizeWord(
      "Value",
      overriddenAttributeValuesCount,
    )} of ${overriddenAttributeValuesCount} ${pluralizeWord(
      "attribute",
      overriddenAttributeValuesCount,
    )} will be overwritten for ${pluralizeWord("sample", collidingSamples.length)}: ${collidingSamples.join(", ")}.`;
  }
  return warningMessage;
}
